/**
 * PermissionErrorScreen.js — boot-time full-screen error for #165.
 *
 * Shown when /permissions-check reports that the current session's CRM
 * role is missing one of the permissions the proxy hard-enforces (today
 * that's just crm.live_chat.ALL, but the list is driven by
 * netlify/functions/config/inbox-required-permissions.js so it can
 * grow). Also surfaces the runtime-enforced perms the CRM validates
 * server-side, as a checklist the admin can verify before handing out
 * the magic link.
 *
 * The goal: instead of the agent seeing a silent 403 when the chat list
 * fails to load, give them a human-readable "your admin needs to grant
 * these in CRM → Team → Roles" message with the exact breadcrumb each
 * missing permission lives under.
 *
 * Retry re-runs the check — useful after the admin grants the role and
 * the agent re-opens the tab. Sign out clears everything so the agent
 * can re-login with a different identifier if this session was minted
 * for the wrong person.
 */

import { html }      from 'htm/preact';
import { useStore }  from '@nanostores/preact';

import { $session, clearSession } from '../stores/session.js';
import { $permissions, checkPermissions } from '../services/permissions-check.js';
import { navigate } from '../stores/ui.js';
import { SCREENS } from '../shared/constants.js';
import { disconnectRealtime } from '../services/realtime.js';
import { unregisterPush } from '../services/push.js';
import * as api from '../services/api.js';

export function PermissionErrorScreen() {
  const perms   = useStore($permissions);
  const session = useStore($session);

  const handleRetry = async () => {
    const next = await checkPermissions();
    if (next.ok) navigate(SCREENS.CHAT_LIST);
  };

  const handleLogout = async () => {
    try { await unregisterPush(); } catch {}
    try { await api.logout(); } catch {}
    disconnectRealtime();
    clearSession();
    navigate(SCREENS.LOGIN);
  };

  const missing         = perms.missing_required || [];
  const runtimeHints    = perms.runtime_hints || [];
  const recommended     = perms.missing_recommended || [];
  const agentLabel      = session?.agentEmail || 'your account';

  return html`
    <div class="inbox-screen inbox-perm-error-screen">
      <div class="inbox-perm-error-card">
        <div class="inbox-perm-error-icon" aria-hidden="true">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.5 3.8 17 5 19 5a1 1 0 0 1 1 1z"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
        </div>

        <h1 class="inbox-perm-error-title">Your role is missing inbox access</h1>
        <p class="inbox-perm-error-sub">
          ${agentLabel} can sign in, but the CRM role assigned to this
          account hasn't been granted the permissions the inbox needs.
          Ask your primary admin to open <strong>CRM → Team → Roles</strong>,
          edit this role, and turn on the items below.
        </p>

        ${missing.length > 0 && html`
          <section class="inbox-perm-error-section inbox-perm-error-section--required">
            <h2 class="inbox-perm-error-section-title">
              <span class="inbox-perm-dot inbox-perm-dot--required"></span>
              Required — grant these
            </h2>
            <ul class="inbox-perm-error-list">
              ${missing.map(p => html`
                <li class="inbox-perm-error-item" key=${p.label}>
                  <div class="inbox-perm-error-item-label">${p.label}</div>
                  <div class="inbox-perm-error-item-path">${p.path}</div>
                  ${p.breaks && html`
                    <div class="inbox-perm-error-item-breaks">${p.breaks}</div>
                  `}
                </li>
              `)}
            </ul>
          </section>
        `}

        ${runtimeHints.length > 0 && html`
          <section class="inbox-perm-error-section inbox-perm-error-section--hints">
            <h2 class="inbox-perm-error-section-title">
              <span class="inbox-perm-dot inbox-perm-dot--hint"></span>
              Also verify these are enabled
            </h2>
            <p class="inbox-perm-error-section-blurb">
              We can't read these from your role directly — the CRM enforces
              them server-side. If the chat list still doesn't load after
              granting the required perms above, your admin should check
              these too.
            </p>
            <ul class="inbox-perm-error-list inbox-perm-error-list--compact">
              ${runtimeHints.map(p => html`
                <li class="inbox-perm-error-item" key=${p.label}>
                  <div class="inbox-perm-error-item-label">${p.label}</div>
                </li>
              `)}
            </ul>
          </section>
        `}

        ${recommended.length > 0 && html`
          <section class="inbox-perm-error-section inbox-perm-error-section--recommended">
            <h2 class="inbox-perm-error-section-title">
              <span class="inbox-perm-dot inbox-perm-dot--recommended"></span>
              Recommended (optional)
            </h2>
            <p class="inbox-perm-error-section-blurb">
              These unlock nicer features but the inbox works without them.
            </p>
            <ul class="inbox-perm-error-list inbox-perm-error-list--compact">
              ${recommended.map(p => html`
                <li class="inbox-perm-error-item" key=${p.label}>
                  <div class="inbox-perm-error-item-label">${p.label}</div>
                </li>
              `)}
            </ul>
          </section>
        `}

        <div class="inbox-perm-error-actions">
          <button class="btn btn-primary inbox-perm-error-retry" onClick=${handleRetry}>
            Check again
          </button>
          <button class="btn btn-ghost inbox-perm-error-logout" onClick=${handleLogout}>
            Sign out
          </button>
        </div>
      </div>
    </div>
  `;
}
